const MAX_DECODE_SIDE = 2000;
const READ_OPTIONS = {
  formats: ['QRCode'],
  tryHarder: true,
  tryRotate: true,
  tryInvert: true,
  tryDownscale: true,
  maxNumberOfSymbols: 1,
};

let readerPromise = null;

// The ~1 MB WASM decoder is only fetched when the scanner is opened.
// It is served from /zxing_reader.wasm (copied into public/).
function getReader() {
  if (!readerPromise) {
    readerPromise = import('zxing-wasm/reader')
      .then(async (reader) => {
        await reader.prepareZXingModule({
          overrides: {
            locateFile: (path, prefix) => (path.endsWith('.wasm') ? '/zxing_reader.wasm' : prefix + path),
          },
          fireImmediately: true,
        });
        return reader;
      })
      .catch((error) => {
        readerPromise = null;
        throw error;
      });
  }
  return readerPromise;
}

export async function decodeQrFromImageData(imageData) {
  const { readBarcodes } = await getReader();
  const [result] = await readBarcodes(imageData, READ_OPTIONS);
  return result?.text || null;
}

export function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image_load_failed'));
    };
    image.src = url;
  });
}

function drawScaled(image, maxSide) {
  const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);
  canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas;
}

// Reads a QR from a photo. Tries a few sizes because the best scale depends on
// how big the QR is in the frame and how noisy the print is.
export async function decodeQrFromFile(file) {
  const image = await loadImage(file);
  for (const side of [MAX_DECODE_SIDE, 1200, 800]) {
    const canvas = drawScaled(image, side);
    const context = canvas.getContext('2d');
    const text = await decodeQrFromImageData(context.getImageData(0, 0, canvas.width, canvas.height));
    if (text) return text;
  }
  return null;
}

// Shrinks phone photos so they fit the 5 MB backend limit. PDFs pass through.
export async function compressInvoiceFile(file, { maxSide = 1800, quality = 0.8 } = {}) {
  if (!file.type.startsWith('image/')) return file;
  try {
    const image = await loadImage(file);
    const canvas = drawScaled(image, maxSide);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) return file;
    const name = file.name.replace(/\.[^.]+$/, '') || 'factura';
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}
